import { firstValueFrom } from 'rxjs';
import { Injectable, signal } from '@angular/core';
import { ScholarshipApiService } from './api/scholarship-api.service';
import { Institution } from '../models/scholarship.model';

const ACTIVE_INSTITUTION_KEY = 'liessin_last_institution_id';

@Injectable({ providedIn: 'root' })
export class InstitutionContextService {
  readonly institutions = signal<Institution[]>([]);
  readonly selectedInstitutionId = signal<number>(this.readStoredInstitutionId());
  readonly loading = signal(false);
  private pending: Promise<number> | null = null;

  ensureInstitution(): Promise<number> {
    if (this.pending) return this.pending;
    this.loading.set(true);
    this.pending = firstValueFrom(this.api.getActiveInstitutions()).then(institutions => {
      this.institutions.set(institutions ?? []);
      if (!institutions?.length) {
        this.selectedInstitutionId.set(0);
        localStorage.removeItem(ACTIVE_INSTITUTION_KEY);
        throw new Error('Sua conta não possui vínculo com uma instituição ativa. Procure a administração.');
      }
      this.ensureSelectedInstitution(institutions);
      return this.selectedInstitutionId();
    }).finally(() => { this.loading.set(false); this.pending = null; });
    return this.pending;
  }

  constructor(private api: ScholarshipApiService) {}

  loadActiveInstitutions(): void {
    void this.ensureInstitution().catch(() => {});
  }

  setSelectedInstitutionId(institutionId: number): void {
    if (!institutionId || Number.isNaN(institutionId)) return;
    this.selectedInstitutionId.set(institutionId);
    localStorage.setItem(ACTIVE_INSTITUTION_KEY, String(institutionId));
  }

  private ensureSelectedInstitution(institutions: Institution[]): void {
    if (institutions.length === 0) return;

    const selected = this.selectedInstitutionId();
    const exists = institutions.some((institution) => institution.id === selected);
    if (!exists) {
      this.setSelectedInstitutionId(institutions[0].id);
    }
  }

  private readStoredInstitutionId(): number {
    const stored = Number(localStorage.getItem(ACTIVE_INSTITUTION_KEY));
    return stored > 0 ? stored : 0;
  }
}
